import { useHasPermission } from '@app/core/auth/authorization/hooks/useHasPermission';
import { isOwnedStation } from '@app/core/stations/helpers';
import { Station } from '@app/core/types/stations';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { fragmentsPermissions } from '@mi-front/api-clients';
import {
  SocialExportGetStationConnectionStatesDto,
  useSocialExportListConnectionsByStation,
} from '@mi-front/api-v2';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { FormattedMessage } from 'react-intl';
import { ExportControl } from '../export/ExportControl';
import { QuickPlatformUploadFields } from './QuickPlatformUploadFields';

type Props = {
  station: Station;
  durationMs?: number;
  allowExternalExport?: boolean;
};

/**
 * Quick-publish platform toggles for fragment creation, gated on station connections.
 */
export function QuickFragmentExportFields({ station, durationMs, allowExternalExport }: Props) {
  const hasSocialPublish = useHasPermission(fragmentsPermissions.Fragments_SocialPublish);
  const connectionsQuery = useSocialExportListConnectionsByStation(station.id, {
    query: { enabled: isOwnedStation(station) && hasSocialPublish },
  });

  if (!isOwnedStation(station) || (!hasSocialPublish && !allowExternalExport)) {
    return null;
  }

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          letterSpacing: '0.09em',
          fontSize: '11px',
          fontWeight: 500,
        }}
      >
        <FormattedMessage id="fragments.form.publishOn" defaultMessage="Publicar rápido" />
      </Typography>
      <Stack spacing={1} mt={1}>
        {hasSocialPublish && (
          <QueryView inline query={connectionsQuery}>
            {(data: SocialExportGetStationConnectionStatesDto) => (
              <>
                {socialPlatforms
                  .all()
                  .filter((platform) =>
                    data.results.some(
                      (connection) =>
                        connection.platform.toUpperCase() === platform.id.toUpperCase(),
                    ),
                  )
                  .map((platform) => (
                    <Box key={platform.id}>
                      <QuickPlatformUploadFields
                        stationId={station.id}
                        platform={platform}
                        durationMs={durationMs}
                      />
                    </Box>
                  ))}
              </>
            )}
          </QueryView>
        )}
        {allowExternalExport && (
          <Grid item lg={12} xl={6}>
            <ExportControl />
          </Grid>
        )}
      </Stack>
    </Box>
  );
}
